import moment from "moment";
import { Link } from "react-router-dom";
import { Grid, Button } from '@mui/material/';
import { log } from "../../utils";
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import KeyIcon from '@mui/icons-material/Key';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useState, useMemo } from 'react';
import { useDataContext } from "../../contexts/DataContext";
import { resolveMachineById } from "../../utils/jobMachines";

export function JobHeader({ job }) {
    const { machines } = useDataContext();
    const [isAccessNotesExpanded, setIsAccessNotesExpanded] = useState(false);

    const machineSummary = useMemo(() => {
        const ids = job?.machine_ids_array;
        if (!Array.isArray(ids) || ids.length === 0) return null;
        const parts = ids
            .map((id) => {
                const m = resolveMachineById(id, machines, job?.venue_machines);
                if (!m?.serial_id) return null;
                const type = m.machine_type ? ` (${m.machine_type})` : "";
                return `${m.serial_id}${type}`;
            })
            .filter(Boolean);
        return parts.length ? parts.join(", ") : null;
    }, [job, machines]);
    
    const handleLinkClick = (e) => {
        e.stopPropagation();
        try {
            // Log the link click event
            //log("Link Clicked", e.target.href);
        } catch (error) {
            // Log the error if logging fails
            log("Error Logging Link Click", error);
        }
    };

    const isTextLong = (text, maxLength = 50) => {
        return text && text.length > maxLength;
    };

    return (
        <>
            <section className="section--job-summary">
                <h3>{job.service_call_type ? job.service_call_type : "Service Call"}</h3>
                <h3>{job.venue_name}</h3>
                {machineSummary && (
                    <p
                        className="font-smaller"
                        style={{
                            margin: "0.35rem 0 0.75rem",
                            fontWeight: 600,
                            color: "var(--bc-persiangreen, #00A49B)",
                        }}
                    >
                        {machineSummary.includes(",") ? "Machines: " : "Machine: "}
                        {machineSummary}
                    </p>
                )}
                <Grid container className="font-smaller">
                    <Grid xs={12} sm={6} item>
                        <address>
                            {job.address}<br />
                            {job.suburb} {job.postal_code} {job.deposit_region}
                        </address>
                        {job.access_notes && (
                            <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
                                <KeyIcon style={{ color: '#666', fontSize: 14, marginRight: 4 }} titleAccess="Access Notes" />
                                <span style={{ fontSize: 12, color: '#666' }}>
                                    {isTextLong(job.access_notes) && !isAccessNotesExpanded ? (
                                        <Button
                                            size="small"
                                            variant="text"
                                            style={{ 
                                                padding: 0, 
                                                minWidth: 'auto', 
                                                textTransform: 'none',
                                                justifyContent: 'flex-start',
                                                textAlign: 'left'
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsAccessNotesExpanded(true);
                                            }}
                                        >
                                            {job.access_notes.substring(0, 50)}...
                                            <ExpandMoreIcon style={{ fontSize: 14, marginLeft: 4 }} />
                                        </Button>
                                    ) : (
                                        <div>
                                            {job.access_notes}
                                            {isTextLong(job.access_notes) && isAccessNotesExpanded && (
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    style={{ 
                                                        padding: 0, 
                                                        minWidth: 'auto', 
                                                        textTransform: 'none',
                                                        justifyContent: 'flex-start',
                                                        textAlign: 'left',
                                                        marginLeft: 4
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setIsAccessNotesExpanded(false);
                                                    }}
                                                >
                                                    <ExpandLessIcon style={{ fontSize: 14 }} />
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </span>
                            </div>
                        )}
                        <div className="flex flex-row items-center">
                            <span className="text-muted" style={{ marginRight: ".5rem" }}>Contact:</span>
                            {job?.contact_name}
                            {job.contact_phone && (
                                <>
                                    {<span style={{ marginLeft: ".75rem" }}>·</span>}
                                    <Link
                                        className="flex flex-row items-center"
                                        style={{ marginLeft: ".75rem" }}
                                        to={`tel:${job.contact_phone}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={handleLinkClick}
                                    >
                                        <PhoneIphoneIcon sx={{ fontSize: 12, marginRight: ".125rem" }} />{job.contact_phone}
                                    </Link>
                                </>
                            )}
                            {job.contact_email && (
                                <>
                                    {job.contact_email.includes(",") ? (
                                        job.contact_email.split(",").map((email, index) => (
                                            <Link
                                                key={index}
                                                className="flex flex-row items-center"
                                                style={{ marginLeft: ".75rem" }}
                                                to={`mailto:${email.trim()}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={handleLinkClick}
                                            >
                                                <MailOutlineIcon sx={{ fontSize: 16, marginRight: ".125rem" }} />
                                            </Link>
                                        ))
                                    ) : (
                                        <Link
                                            className="flex flex-row items-center"
                                            style={{ marginLeft: ".75rem" }}
                                            to={`mailto:${job.contact_email}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={handleLinkClick}
                                        >
                                            <MailOutlineIcon sx={{ fontSize: 16, marginRight: ".125rem" }} />
                                        </Link>
                                    )}
                                </>
                            )}
                            {job.contact_number && (
                                <>
                                    <Link
                                        className="flex flex-row items-center"
                                        style={{ marginLeft: ".75rem" }}
                                        to={`tel:${job.contact_number}`}
                                        target="_blank"
                                        onClick={handleLinkClick}
                                    >
                                        <PhoneIphoneIcon sx={{ fontSize: 16, marginRight: ".125rem" }} />
                                    </Link>
                                </>
                            )}
                        </div>
                    </Grid>
                    <Grid xs={12} sm={6} item sx={{ textAlign: { xs: "left", sm: "right" } }}>
                        <div><span className="text-muted">Assigned by:</span> {job.created_by_name}</div>
                        <div><span className="text-muted">Promised:</span> {moment(job.promise_date * 1000).format('MMMM DD, YYYY')}</div>
                        <div><span className="text-muted" style={{ textTransform: "capitalize" }}>Status:</span> <span style={{ textTransform: "capitalize" }}>{job.status}</span></div>
                    </Grid>
                </Grid>
            </section>
        </>
    );
}

export default JobHeader;